/**
 * Agent id of the Quill custom agent built on the Rain runtime. This is the
 * editor-side chat agent (automation-scoped). See agent-quill's `quill.k8s.yaml`.
 */
export const RAIN_QUILL_AGENT_ID = 'quill2';

// The generated artifact hooks interpolate `${artifactUri}` directly into both
// the request path and the React-Query key, so every call site must agree on a
// single encoded form for cache invalidation to match.
// `aop.md` is a non-reserved artifact (like blueprint/aop.md), so a built automation's
// AOP lives in the `artifacts` map under this name — used when reading a
// revision snapshot whose `english_code` projection isn't populated.
export const AOP_ARTIFACT_NAME = 'aop.md';
export const AOP_ARTIFACT_URI = `grimoire://${AOP_ARTIFACT_NAME}`;
export const AOP_ARTIFACT_URI_ENCODED = encodeURIComponent(AOP_ARTIFACT_URI);

export const CODE_MANIFEST_ARTIFACT_URI = 'grimoire://code_manifest.json';
export const CODE_MANIFEST_ARTIFACT_URI_ENCODED = encodeURIComponent(
  CODE_MANIFEST_ARTIFACT_URI,
);

/**
 * The blueprint SOP document. `english_code` (grimoire://aop.md) is canonical —
 * generated from SPy by quill at build time — so the not-yet-built blueprint
 * lives as a separate entry in the automation's `artifacts` map, in its own
 * `blueprint/` folder (alongside the template seeds). Grimoire exposes
 * non-reserved artifact names to the agent as `grimoire://<name>`, so quill
 * reads this as `grimoire://blueprint/aop.md` (its requirements document for the
 * build). Values in the artifacts map are base64-encoded bytes.
 */
export const BLUEPRINT_ARTIFACT_NAME = 'blueprint/aop.md';
export const BLUEPRINT_ARTIFACT_URI = `grimoire://${BLUEPRINT_ARTIFACT_NAME}`;
// Encoded once so the thread artifact endpoints (`:content`, `:diff`) and their
// React-Query keys agree on a single form (mirrors AOP_ARTIFACT_URI_ENCODED).
export const BLUEPRINT_ARTIFACT_URI_ENCODED = encodeURIComponent(
  BLUEPRINT_ARTIFACT_URI,
);

/**
 * Which run the step view is showing, and how to change it. Owned by the editor,
 * not by the thread, so it is closed over when the renderer is built rather than
 * read off the render context.
 */
export interface IQuillTestRunFocus {
  /** Tool-call id of the run currently on the step view, if any. */
  focusedToolCallId?: string;
  /**
   * Focus the step view on a run. Called with `undefined` when the already
   * focused card is clicked again, to close the run view.
   */
  onFocus?: (
    run:
      | { toolCallId: string; runId: string; startedAtMs?: number }
      | undefined,
  ) => void;
}

/**
 * Quill artifact tools. `artifact_write` / `artifact_edit` stage an uncommitted
 * change, which the editor surfaces as a diff rather than a chat card; a
 * successful `save_automation` means the committed automation changed, so the
 * editor refetches it.
 */
export enum ERainArtifactTool {
  ArtifactWrite = 'artifact_write',
  ArtifactEdit = 'artifact_edit',
  PrepareCodeManifest = 'prepare_code_manifest',
  SaveAutomation = 'save_automation',
}

/**
 * The tools quill2 surfaces as chat cards — its automation build loop, plus the
 * bdk and sandbox tools it reaches for while filling one in.
 *
 * Names MUST match the tool definitions in agent-quill's `quill.k8s.yaml`.
 */
export enum EQuillVisibleTool {
  // Build + commit.
  PrepareCodeManifest = 'prepare_code_manifest',
  SaveAutomation = 'save_automation',
  // The build-loop test tools (each renders a "view run" card).
  TestAutomation = 'test_automation',
  ReplayRun = 'replay_run',
  // The sandbox probe, labelled by its `purpose` argument.
  RunCode = 'run_code',
  // Collections + assets the built automation reads and writes.
  ListCollections = 'list_collections',
  CreateCollection = 'create_collection',
  UpdateCollectionSchema = 'update_collection_schema',
  ListAssets = 'list_assets',
  CreateAsset = 'create_asset',
  // bdk integration catalog.
  SearchApps = 'search_apps',
  SearchProcedures = 'search_procedures',
  RequestConnection = 'request_connection',
  // Live browser screencast, emitted by the browser-author sub-agent once it
  // has provisioned a session. Auto-acknowledged by the client rather than by
  // a click — see `quill.config.ts`.
  ShowBrowserSession = 'show_browser_session',
  // Discovery.
  BrowseDiscoverables = 'browse_discoverables',
  SetDiscovered = 'set_discovered',
  // Web research.
  WebSearch = 'web_search',
  WebFetch = 'web_fetch',
}

/**
 * Tools quill2 emits and deliberately does not surface. Listing one changes
 * nothing the user sees — anything outside {@link EQuillVisibleTool} renders no
 * card either way, and any invisible tool still drives the generic working
 * indicator while it runs. The list exists so `reportDroppedToolCall` stays
 * worth reading: with the known-internal tools declared, a warning means "the
 * CR grew a tool nobody has classified" rather than naming one of the usual
 * dozen.
 *
 * So every entry here is a claim that the tool's work is already visible
 * somewhere else, or that it has nothing a reader could act on.
 *
 * Names MUST match the tool definitions in agent-quill's `quill.k8s.yaml`.
 */
export enum EQuillInternalTool {
  // Run inspection: telemetry about a run the step view is already showing.
  GetRun = 'get_run',
  GetRunTrace = 'get_run_trace',
  ListRuns = 'list_runs',
  ListRevisions = 'list_revisions',
  GetRevision = 'get_revision',
  // bdk catalog reads. Whatever they found reaches the user through the card
  // that follows — a connection picker, a discovery approval, generated code.
  GetConnection = 'get_connection',
  ListConnections = 'list_connections',
  ListConnectionProcedures = 'list_connection_procedures',
  ListBrowserProcedures = 'list_browser_procedures',
  // Library reads behind the collection and asset cards, which show what they
  // resolved to.
  GetCollection = 'get_collection',
  GetCollectionSchema = 'get_collection_schema',
  GetFolderTree = 'get_folder_tree',
  // Reads of a file the user supplied. These fire in bursts while quill works
  // out a spreadsheet's shape, so a row each would bury the transcript.
  ExcelInspect = 'excel_inspect',
  ExcelSheetInspect = 'excel_sheet_inspect',
  ExcelCellRead = 'excel_cell_read',
  ExcelRangeRead = 'excel_range_read',
  ExcelSearch = 'excel_search',
  TableInspect = 'table_inspect',
  TableQuery = 'table_query',
}
